import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PersonIcon from "@mui/icons-material/Person";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import { Alert, Avatar, Box, Button, CircularProgress, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { MESSAGE_TYPES, type DesignerMessage } from "../UserInterfaceModule/Types/DesignerMessages";
import { resolveDesignerOrigin } from "../UserInterfaceModule/Utils/designerAuthentication";
import { IsDesignMode } from "../UserInterfaceModule/Utils/runtimeDetection";
import AuthContext from "./AuthContext";
import ConfigContext from "./ConfigContext";

/** User-initiated popup auth; no credentials or access tokens cross the designer message bridge. */
export default function DesignerAuthenticationControls() {
  const auth = useContext(AuthContext);
  const config = useContext(ConfigContext);
  const [busy, setBusy] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [error, setError] = useState<string>();
  if (!IsDesignMode) return null;
  const userName: string = auth.isAuthenticated ? auth.account?.name || auth.account?.displayName || auth.account?.username || "User" : "Guest";
  const initials = userName.trim().split(/\s+/).map((part) => part[0]).join("").toUpperCase().slice(0, 2);
  const avatar = (
    <Avatar aria-label={userName} sx={{ width: 28, height: 28, fontSize: 12 }}>
      {auth.isAuthenticated ? initials : <PersonIcon fontSize="small" />}
    </Avatar>
  );
  const action = async () => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    const authenticated = !auth.isAuthenticated;
    try {
      const popup = authenticated ? auth.authService.signInPopup : auth.authService.signOutPopup;
      if (!popup) throw new Error("Popup authentication is unavailable.");
      await popup.call(auth.authService);
      const origin = resolveDesignerOrigin(config?.DESIGNER_ORIGIN);
      if (origin)
        window.parent.postMessage({ type: MESSAGE_TYPES.AUTH_STATE_CHANGED, payload: { isAuthenticated: authenticated } } satisfies DesignerMessage, origin);
    } catch {
      setError("Authentication did not complete. Allow popups for the runtime and try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{ position: "fixed", bottom: 12, left: 12, zIndex: 1500, maxWidth: "calc(100% - 24px)" }}>
      {error && (
        <Alert severity="warning" onClose={() => setError(undefined)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      {minimized ? (
        <Paper elevation={6} sx={{ borderRadius: "50%" }}>
          <Tooltip title={`Runtime ${auth.isAuthenticated ? "signed in" : "signed out"} — show authentication controls`}>
            <IconButton aria-label="Show authentication controls" onClick={() => setMinimized(false)}>
              {avatar}
            </IconButton>
          </Tooltip>
        </Paper>
      ) : (
        <Paper elevation={6} sx={{ px: 1.5, py: 1, borderRadius: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Tooltip title={userName}>{avatar}</Tooltip>
            <Typography variant="caption">Runtime {auth.isAuthenticated ? "signed in" : "signed out"}</Typography>
            <Tooltip title={auth.isAuthenticated ? "Sign out of the runtime preview" : "Sign in to the runtime without leaving the designer"}>
              <span>
                <Button
                  size="small"
                  disabled={busy}
                  aria-label={auth.isAuthenticated ? "Sign out of preview" : "Sign in to preview"}
                  startIcon={busy ? <CircularProgress size={14} /> : auth.isAuthenticated ? <LogoutIcon /> : <LoginIcon />}
                  onClick={() => void action()}
                >
                  {auth.isAuthenticated ? "Sign out" : "Sign in"}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Minimize authentication controls">
              <span>
                <IconButton size="small" disabled={busy} aria-label="Minimize authentication controls" onClick={() => setMinimized(true)}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
